<script setup lang="ts">
import { computed } from 'vue';

interface RadioOption {
  label: string;
  value: string;
  description?: string;
  disabled?: boolean;
}

const props = defineProps<{
  modelValue: string;
  options: RadioOption[];
  name?: string;
  vertical?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits(['update:modelValue']);

const selectedValue = computed({
  get: () => props.modelValue,
  set: (value: string) => {
    if (!props.disabled) {
      emit('update:modelValue', value);
    }
  }
});

const groupName = props.name || `radio-group-${Math.random().toString(36).substring(2, 9)}`;

const isOptionDisabled = (option: RadioOption) => {
  return props.disabled || option.disabled;
};
</script>

<template>
  <div :class="['radio-group', vertical ? 'flex flex-col gap-4' : 'flex gap-6']">
    <label
      v-for="option in options"
      :key="option.value"
      :class="[
        'flex select-none items-start p-4 border rounded-md transition-colors relative',
        selectedValue === option.value 
          ? 'border-brand-500 bg-brand-500/8 dark:bg-brand-500/3' 
          : 'border-gray-300 hover:border-gray-400 dark:border-gray-700 hover:dark:border-gray-600 bg-transparent dark:bg-gray-900',
        // Disabled states
        isOptionDisabled(option) 
          ? 'opacity-60 cursor-not-allowed pointer-events-none' 
          : 'cursor-pointer'
      ]"
    >
      <div class="flex items-start h-5 mt-0.5">
        <input
          type="radio"
          :name="groupName"
          :value="option.value"
          v-model="selectedValue"
          :disabled="isOptionDisabled(option)"
          class="sr-only peer"
        />
        <div
          :class="[
            'flex h-5 w-5 items-center justify-center rounded-full border mr-3 flex-shrink-0 transition-colors',
            selectedValue === option.value 
              ? isOptionDisabled(option)
                ? 'border-gray-400 bg-gray-200 dark:bg-gray-700'
                : 'border-primary bg-gray dark:bg-transparent'
              : isOptionDisabled(option)
                ? 'border-gray-300 bg-gray-100 dark:bg-gray-800'
                : 'border-gray-400'
          ]"
        >
          <span
            :class="[
              'transition-opacity duration-200',
              selectedValue === option.value ? 'opacity-100' : 'opacity-0',
              isOptionDisabled(option) ? 'text-gray-500' : 'text-current'
            ]"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="10" cy="10" r="5" :fill="isOptionDisabled(option) ? '#9CA3AF' : '#3056D3'"></circle>
            </svg>
          </span>
        </div>
      </div>
      <div class="flex-1">
        <div 
          :class="[
            'font-medium',
            isOptionDisabled(option) 
              ? 'text-gray-500 dark:text-gray-400' 
              : 'text-gray-900 dark:text-white'
          ]"
        >
          {{ option.label }}
        </div>
        <div 
          v-if="option.description" 
          :class="[
            'text-sm mt-1',
            isOptionDisabled(option)
              ? 'text-gray-400 dark:text-gray-500'
              : 'text-gray-500'
          ]"
        >
          {{ option.description }}
        </div>
      </div>
      
      <!-- Focus ring that shows only on keyboard focus -->
      <div 
        class="absolute inset-0 rounded-md border-2 border-transparent peer-focus:border-blue-500 peer-focus:dark:border-blue-400 pointer-events-none transition-all duration-200"
        :class="{
          'peer-focus:border-blue-500 peer-focus:dark:border-blue-400': !isOptionDisabled(option)
        }"
      />
    </label>
  </div>
</template>